/**
 * What is assigned to you, and where you stand on each.
 *
 * The page a nurse opens. Overdue first, then due soonest; completed and
 * waived at the bottom as the record of what was done.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { useAddons } from '../org/useAddons';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import { isCallFailure } from '../../lib/callable';
import { daysUntil, formatDay } from '../../shared/dates';
import { useMyAssignments } from './useCompetencies';
import { ASSIGNMENT_LABEL, ASSIGNMENT_TONE, assignmentActions, type Assignment } from './types';
import { LOCKED_TITLE } from './CompetencyListPage';
import { SUBMITTED_KEY } from './EvidencePage';
import styles from '../../shared/Page.module.css';
import own from './Competencies.module.css';

const ORDER: Record<Assignment['status'], number> = { overdue: 0, pending: 1, completed: 2, waived: 3 };

export function sortAssignments(rows: readonly Assignment[]): Assignment[] {
  return [...rows].sort((a, b) =>
    ORDER[a.status] - ORDER[b.status] || (a.dueDate ?? '').localeCompare(b.dueDate ?? ''));
}

export function dueText(a: Assignment): { text: string; cls?: string } {
  if (!a.dueDate) return { text: '—' };
  const days = daysUntil(a.dueDate);
  const open = a.status === 'pending' || a.status === 'overdue';
  if (open && days !== null && days < 0) return { text: `${formatDay(a.dueDate)} · ${-days} ${days === -1 ? 'day' : 'days'} ago`, cls: 'past' };
  if (open && days !== null && days <= 14) return { text: `${formatDay(a.dueDate)} · ${days === 0 ? 'today' : `${days} ${days === 1 ? 'day' : 'days'}`}`, cls: 'soon' };
  return { text: formatDay(a.dueDate) };
}

export function MyCompetenciesPage() {
  const { session } = useAuth();
  const orgId = session?.orgId ?? null;
  const uid = session?.uid ?? null;
  const roles = session?.roles ?? [];
  const addons = useAddons(orgId);
  const enabled = addons.data?.competencyEvidence === true;
  const mine = useMyAssignments(orgId, uid, enabled);
  const rows = useMemo(() => sortAssignments(mine.data ?? []), [mine.data]);
  // The evidence page leaves a word here on its way out; said once, then gone.
  const [submitted] = useState<string | null>(() => {
    try {
      const m = window.sessionStorage.getItem(SUBMITTED_KEY);
      if (m) window.sessionStorage.removeItem(SUBMITTED_KEY);
      return m;
    } catch { return null; }
  });

  if (addons.isLoading) return <Spinner label="Loading" />;
  if (!enabled) {
    return (
      <div className={styles.body}>
        <PageState title={LOCKED_TITLE}>This add-on is not enabled for your organization.</PageState>
      </div>
    );
  }

  const open = rows.filter((a) => a.status === 'pending' || a.status === 'overdue').length;

  return (
    <>
      <header className={styles.header}>
        <div className={styles.headerRow}>
          <div>
            <h1 className={styles.title}>Your assignments</h1>
            <p className={styles.subtitle}>
              {mine.isLoading ? 'Loading…' : open === 0 ? 'Nothing is waiting on you.' : `${open} ${open === 1 ? 'competency needs' : 'competencies need'} your evidence.`}
            </p>
          </div>
          <div className={styles.actions}>
            <Link to="/competencies" className={styles.btn}>All competencies</Link>
          </div>
        </div>
      </header>

      <div className={styles.body}>
        {submitted && <div className={`${styles.notice} ${styles.noticeInfo}`} role="status">{submitted}</div>}
        {mine.error && (
          <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
            Could not load your assignments. {isCallFailure(mine.error) ? mine.error.message : ''}
          </div>
        )}
        {mine.isLoading ? (
          <Spinner label="Loading your assignments" />
        ) : rows.length === 0 ? (
          <div className={`${styles.notice} ${styles.noticeInfo}`} role="status">
            Nothing has been assigned to you. When a competency that applies to your role or department is published, it appears here with a due date.
          </div>
        ) : (
          <div className={own.tableWrap}>
            <table className={own.table}>
              <thead>
                <tr>
                  <th scope="col">Competency</th>
                  <th scope="col">Status</th>
                  <th scope="col">Due</th>
                  <th scope="col">Done</th>
                  <th scope="col"><span className="vh">Action</span></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => {
                  const can = assignmentActions(a, roles, uid);
                  const due = dueText(a);
                  return (
                    <tr key={a.id}>
                      <td className={own.titleCell}>
                        <Link to={`/competencies/${a.competencyId}`}>{a.competencyTitle}</Link>
                        <span className={own.sub}>version {a.competencyVersionNumber}</span>
                      </td>
                      <td>
                        <span className={`${styles.tag} ${styles[ASSIGNMENT_TONE[a.status]]}`}>{ASSIGNMENT_LABEL[a.status]}</span>
                        {can.awaitingApproval && <span className={own.sub}>Evidence submitted — awaiting approval</span>}
                        {a.status === 'waived' && a.waivedReason && <span className={own.sub}>{a.waivedReason}</span>}
                      </td>
                      <td className={`${own.num} ${due.cls ? own[due.cls] : ''}`}>{due.text}</td>
                      <td className={`${own.num} ${a.completedAt ? '' : own.muted}`}>{formatDay(a.completedAt)}</td>
                      <td>
                        {can.submit && (
                          <Link to={`/competencies/assignments/${a.id}`} className={`${styles.btn} ${styles.btnPrimary}`}>Submit evidence</Link>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
